import { useState, useEffect, useRef } from 'react';
import { PostCard } from './PostCard';
import { CreatePost } from './CreatePost';
import { usePosts } from '@/hooks/usePosts';
import { useInView } from 'react-intersection-observer';
import BeltIcon from '@/assets/images/icons/belt.png';
import { useMyConnections } from '@/hooks/useMyConnections';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Filter as FilterIcon, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InterestsSelector } from '@/components/ui/InterestsSelector';
import { useI18n } from '@/contexts/I18nContext';
import { EmptyState } from '@/components/ui/EmptyState';
import { useFeedFilters } from '@/contexts/FeedFilterContext';

const TABS = [
  { key: 'public', label: 'Whole Community' },
  { key: 'connections', label: 'My Connections' },
  { key: 'gym', label: 'My Gym' },
];

function useMyProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<any>(null);
  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    supabase
      .from('profiles')
      .select('id, main_academy_id')
      .eq('id', userId)
      .single()
      .then(({ data }) => {
        if (mounted) setProfile(data);
      });
    return () => {
      mounted = false;
    };
  }, [userId]);
  return profile;
}

export const SocialFeed = () => {
  const { ref, inView } = useInView();
  const [deletedPostIds, setDeletedPostIds] = useState<string[]>([]);
  const { user } = useAuth();
  const connectionsQuery = useMyConnections();
  const connections = connectionsQuery.data?.pages?.flat() || [];
  const connectionsLoading = connectionsQuery.isLoading;
  const myId = user?.id;
  const myProfile = useMyProfile(myId);
  const myAcademyId = myProfile?.main_academy_id;
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedHobbies, setSelectedHobbies] = useState<string[]>([]);
  const [instructorsOnly, setInstructorsOnly] = useState(false);
  const scrollBarWidthRef = useRef(0);
  const { t } = useI18n();
  
  // Use the feed filters context
  const { 
    filters, 
    updateFilters, 
    clearFilters, 
    hasActiveFilters 
  } = useFeedFilters();
  
  const { activeTab, selectedInterests, appliedInstructorsOnly } = filters;

  // Sync modal state with applied filters when modal opens
  useEffect(() => {
    if (isFilterModalOpen) {
      setSelectedHobbies(selectedInterests);
      setInstructorsOnly(appliedInstructorsOnly);
    }
  }, [isFilterModalOpen, selectedInterests, appliedInstructorsOnly]);

  const tabs = [
    { key: 'public', label: t('social.whole_community') },
    { key: 'connections', label: t('social.my_connections') },
    { key: 'academy', label: t('social.my_academy') },
  ];



  const postsQuery =
    activeTab === 'public'
      ? usePosts({ visibility: 'public', extraKey: [myAcademyId, selectedInterests, appliedInstructorsOnly], interests: selectedInterests, instructorsOnly: appliedInstructorsOnly })
      : activeTab === 'connections'
      ? usePosts({ visibilities: ['connections', 'public'], extraKey: [myAcademyId, selectedInterests, appliedInstructorsOnly], interests: selectedInterests, instructorsOnly: appliedInstructorsOnly })
      : activeTab === 'gym'
      ? usePosts({ visibilities: ['public', 'gym'], extraKey: [myAcademyId, selectedInterests, appliedInstructorsOnly], interests: selectedInterests, instructorsOnly: appliedInstructorsOnly })
      : usePosts({ extraKey: [myAcademyId, selectedInterests, appliedInstructorsOnly], interests: selectedInterests, instructorsOnly: appliedInstructorsOnly });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = postsQuery;

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDeletePost = (postId: string) => {
    setDeletedPostIds((prev) => [...prev, postId]);
  };

  const connectionIds = connections.map((c) => c.id);

  function filterPosts(posts) {
    if (activeTab === 'public') {
      // Whole Community: Show all posts marked as 'Whole Community' (public) by any user.
      return posts.filter(post => post.visibility === 'public');
    }
    if (activeTab === 'connections') {
      if (connectionsLoading) {
        return [];
      }
      // My Connections:
      // - Posts marked as 'Only my connections' or 'Whole Community' by my connections
      // - My own posts marked as 'Only my connections'
      return posts.filter(
        (post) => (
          // Posts from my connections
          (connectionIds.includes(post.user_id) && (post.visibility === 'connections' || post.visibility === 'public'))
          // My own posts marked as 'Only my connections'
          || (String(post.user_id) === String(myId) && post.visibility === 'connections')
        )
      );
    }
    if (activeTab === 'gym') {
      // My Gym:
      // - Posts marked as 'Only my gym' or 'Whole Community' by other users linked to the same academy as me
      // - My own posts marked as 'Only my gym'
      return posts.filter((post) => {
        const isSameAcademy = myAcademyId && String(post.author.main_academy_id) === String(myAcademyId);
        return (
          // Posts from others in my academy
          (post.user_id !== myId && isSameAcademy && (post.visibility === 'gym' || post.visibility === 'public'))
          // My own posts marked as 'Only my gym'
          || (String(post.user_id) === String(myId) && post.visibility === 'gym')
        );
      });
    }
    return posts;
  }

  function isFeedEmpty() {
    if (!data?.pages || data.pages.length === 0) return true;
    
    // Verifica se há posts após aplicar filtros e remover posts deletados
    const hasPosts = data.pages.some(page => {
      const filteredPosts = filterPosts(page).filter(post => !deletedPostIds.includes(post.id));
      return filteredPosts.length > 0;
    });
    
    return !hasPosts;
  }

  return (
    <div className="space-y-6">
      <CreatePost />
      
      {/* Abas + Botão de Filtro */}
      <div className="flex items-center px-1 pr-[20px] border-b border-[#332233] mb-2 w-full">
        <div className="flex space-x-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`pb-2 text-[11px] font-semibold transition-colors duration-150 border-b-2 whitespace-nowrap ${
                activeTab === tab.key
                  ? 'text-white border-sossiety-red-600'
                  : 'text-gray-400 border-transparent bg-transparent hover:text-white'
              }`}
              onClick={() => {
                updateFilters({ activeTab: tab.key });
                setSelectedHobbies([]);
                setInstructorsOnly(false);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Botão de filtro */}
        <button
          className={`ml-[16px] flex items-center px-2 py-1 focus:outline-none mt-[-2px] rounded ${hasActiveFilters ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setIsFilterModalOpen(true)}
          aria-label="Open filters"
        >
          <FilterIcon className={`w-4 h-4 ${hasActiveFilters ? 'text-white' : ''}`} />
          {hasActiveFilters && <span className="ml-0.5 text-xs font-bold">•</span>}
        </button>
        
        {/* Indicador de filtros ativos */}
        {hasActiveFilters && (
          <div className="ml-2 flex items-center gap-1">
            {selectedInterests.length > 0 && (
              <span className="text-xs text-white bg-white/20 px-2 py-1 rounded">
                {selectedInterests.length} interests
              </span>
            )}
            {appliedInstructorsOnly && (
              <span className="text-xs text-white bg-white/20 px-2 py-1 rounded">
                Instructors only
              </span>
            )}
          </div>
        )}
      </div>

      {/* Modal de Filtros */}
      <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
        <DialogContent className="bg-white text-[#222] w-[80vw] max-w-lg rounded-xl z-50 p-8 shadow-2xl border-0">
          <DialogHeader>
            <DialogTitle className="text-[#222] font-sans text-lg">{t('social.post_details')}</DialogTitle>
            <DialogDescription className="text-gray-600 text-sm">
              View post details and interactions
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-6 mt-4">
            {/* Hobbies */}
            <div>
              <div className="mb-1 text-sm font-semibold text-black">Interests</div>
              <InterestsSelector
                selected={selectedHobbies}
                onChange={setSelectedHobbies}
                maxSelected={5}
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedHobbies.map(hobby => (
                  <Badge key={hobby} variant="secondary">{hobby}</Badge>
                ))}
              </div>
            </div>
            {/* Instrutores */}
            <div className="flex items-center gap-3">
              <Switch checked={instructorsOnly} onCheckedChange={setInstructorsOnly} id="instructors-toggle" />
              <label htmlFor="instructors-toggle" className="text-sm font-semibold text-[#222] select-none cursor-pointer">Only instructors</label>
            </div>
          </div>
          {/* Botões de ação */}
          <div className="flex flex-row gap-2 w-full mt-8">
            <Button variant="outline" className="rounded-full border-2 border-black text-black font-semibold px-6 py-2 w-1/2" onClick={() => { 
              setSelectedHobbies([]); 
              setInstructorsOnly(false); 
              clearFilters();
            }}>Clear</Button>
            <Button variant="default" className="rounded-full bg-[#D72E16] hover:bg-[#b91c1c] text-white font-semibold px-6 py-2 w-1/2" onClick={() => {
              updateFilters({
                selectedInterests: selectedHobbies,
                appliedInstructorsOnly: instructorsOnly
              });
              setIsFilterModalOpen(false);
            }}>Apply</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-white text-lg font-medium">{t('loading.feed')}</div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="text-center text-red-400 py-4">
          Failed to load posts. Please try again.
        </div>
      )}

      {/* Conteúdo da aba ativa */}
      {!isLoading && !isError && (
        <div className="space-y-0 px-1">
          {data?.pages.map((page, pageIdx) =>
            filterPosts(page)
              .filter(post => !deletedPostIds.includes(post.id))
              .map((post, idx) => {
                return (
                  <div key={post.id}>
                    <PostCard post={post} onDelete={handleDeletePost} />
                    {/* Divider between posts, except the last */}
                    {(idx < page.length - 1 || pageIdx < data.pages.length - 1) && (
                      <div className="relative flex items-center my-2">
                        <div className="flex-grow border-t border-[#332233]" />
                        <img src={BeltIcon} alt="Belt" className="h-5 w-7 mx-2" />
                        <div className="flex-grow border-t border-[#332233]" />
                      </div>
                    )}
                  </div>
                );
              })
          )}

          {/* Loading indicator for next page */}
          <div ref={ref} className="h-10 flex items-center justify-center">
            {isFetchingNextPage && (
              <div className="text-gray-400">Loading more posts...</div>
            )}
          </div>

          {/* Empty feed message */}
          {isFeedEmpty() && (
            !user ? (
              <div className="text-center text-gray-400 py-4 space-y-4">
                <div>Create an account to publish and connect with your friends to view new publications!</div>
              </div>
            ) : (
              activeTab === 'connections' ? (
                !connectionsLoading && (
                  <EmptyState
                    icon={Users}
                    title={t('social.empty_connections.title')}
                    subtitle={t('social.empty_connections.subtitle')}
                    className="text-gray-400"
                  />
                )
              ) : (
                <EmptyState
                  icon={Users}
                  title={t('social.empty_posts.title')}
                  subtitle={t('social.empty_posts.subtitle')}
                  className="text-gray-400"
                />
              )
            )
          )}
        </div>
      )}
    </div>
  );
};
